(() => {
  const GIFT_VARIANT_ID = 45101310312639;
  const SNAPSHOT_KEY_PREFIX = "prefill_qualifiers_v6:"; // + cart.token

  async function fetchCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) throw new Error("Failed to fetch cart");
    return res.json();
  }

  async function changeByKey(lineItemKey, quantity) {
    if (!lineItemKey) return; // prevents 400s
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: lineItemKey, quantity }),
    });
    if (!res.ok) throw new Error("Failed to change item");
    return res.json();
  }

  function dawnRefresh() {
    document.dispatchEvent(new CustomEvent("cart:refresh"));
  }

  function snapshotStorageKey(cart) {
    return `${SNAPSHOT_KEY_PREFIX}${cart.token || "no-token"}`;
  }

  function readSnapshot(cart) {
    try {
      const raw = localStorage.getItem(snapshotStorageKey(cart));
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function writeSnapshot(cart, ids) {
    try {
      localStorage.setItem(snapshotStorageKey(cart), JSON.stringify(ids));
    } catch {}
  }

  function getGiftItems(cart) {
    return cart.items.filter((i) => Number(i.variant_id) === Number(GIFT_VARIANT_ID));
  }

  function getNonGiftItems(cart) {
    return cart.items.filter((i) => Number(i.variant_id) !== Number(GIFT_VARIANT_ID));
  }

  function cartHasAllSnapshotItems(cart, snapshot) {
    if (!Array.isArray(snapshot) || snapshot.length === 0) return false;
    const current = new Set(cart.items.map((i) => Number(i.variant_id)));
    return snapshot.every((id) => current.has(Number(id)));
  }

  async function removeGift(cart) {
    const gifts = getGiftItems(cart);
    for (const g of gifts) {
      await changeByKey(g.key, 0);
    }
    dawnRefresh();
  }

  async function normalizeGiftQty(cart) {
    const gifts = getGiftItems(cart);
    if (!gifts.length) return;

    // remove duplicates
    for (let i = 1; i < gifts.length; i++) {
      await changeByKey(gifts[i].key, 0);
    }
    // force qty 1
    if (gifts[0].quantity !== 1) {
      await changeByKey(gifts[0].key, 1);
    }

    dawnRefresh();
  }

  let running = false;

  async function enforceGiftRules() {
    if (running) return;
    running = true;

    try {
      const cart = await fetchCart();
      const gifts = getGiftItems(cart);

      if (!gifts.length) return;

      // Rule: gift can never exist alone
      const onlyGift =
        cart.items.length === gifts.length &&
        cart.items.every((i) => Number(i.variant_id) === Number(GIFT_VARIANT_ID));

      if (onlyGift) {
        await removeGift(cart);
        return;
      }

      // Snapshot: create only when we have non-gift items (prefilled products)
      let snapshot = readSnapshot(cart);
      if (!snapshot) {
        const nonGift = getNonGiftItems(cart);
        if (!nonGift.length) {
          await removeGift(cart);
          return;
        }
        snapshot = nonGift.map((i) => Number(i.variant_id));
        writeSnapshot(cart, snapshot);
      }

      // STRICT RULE: gift allowed only if ALL prefilled products still exist
      if (!cartHasAllSnapshotItems(cart, snapshot)) {
        await removeGift(cart);
        return;
      }

      // Enforce qty = 1
      await normalizeGiftQty(cart);
    } catch (e) {
      // silent fail
    } finally {
      running = false;
    }
  }

  // Run on load + whenever Dawn refreshes cart sections
  document.addEventListener("DOMContentLoaded", enforceGiftRules);
  document.addEventListener("cart:refresh", () => setTimeout(enforceGiftRules, 50));
  document.addEventListener("cart:updated", () => setTimeout(enforceGiftRules, 50));

  // Catch all cart ajax updates
  const _fetch = window.fetch;
  window.fetch = async (...args) => {
    const res = await _fetch(...args);
    try {
      const url = typeof args[0] === "string" ? args[0] : args[0]?.url;
      if (url && /\/cart\/(add|change|update)\.js/.test(url)) {
        setTimeout(enforceGiftRules, 120);
      }
    } catch {}
    return res;
  };
})();